import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://minnyeelbil.dk";
const SITE_NAME = "Min Nye Elbil";

export interface SeoProps {
  title: string;
  description?: string;
  path?: string;
  image?: string;
  type?: "website" | "article" | "product";
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
  publishedTime?: string;
  modifiedTime?: string;
  articleSection?: string;
  articleTags?: string[];
}

export function Seo({ title, description, path, image, type = "website", jsonLd, noindex, publishedTime, modifiedTime, articleSection, articleTags }: SeoProps) {
  // Keep <title> under 60 chars: only append site name if it fits, otherwise truncate raw title
  const TITLE_MAX = 60;
  const withSuffix = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  let fullTitle = withSuffix;
  if (fullTitle.length > TITLE_MAX) {
    fullTitle = title.length <= TITLE_MAX ? title : title.slice(0, TITLE_MAX - 1).trimEnd() + "…";
  }
  const url = path ? `${SITE_URL}${path.startsWith("/") ? path : "/" + path}` : SITE_URL;
  const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  // Helmet dedup'er kun egne tags. Fjern statiske delings-tags, når ruten
  // har sin egen værdi, men bevar fallback-billedet på sider uden eget billede.
  useEffect(() => {
    const cleanFallbacks = () => document
      .querySelectorAll('meta[name="description"]:not([data-rh]), link[rel="canonical"]:not([data-rh]), meta[property^="og:"]:not([data-rh]), meta[name^="twitter:"]:not([data-rh]), meta[name="robots"]:not([data-rh])')
      .forEach((el) => {
        const property = el.getAttribute('property');
        const name = el.getAttribute('name');
        // Statisk robots fjernes kun, når ruten selv sætter robots (fx noindex på 404)
        if (name === 'robots') { if (document.querySelector('meta[name="robots"][data-rh]')) el.remove(); return; }
        const socialKey = property || (name?.startsWith('twitter:') ? name : null);
        if (socialKey) {
          const attribute = property ? 'property' : 'name';
          if (!document.querySelector(`meta[${attribute}="${socialKey}"][data-rh]`)) return;
        }
        el.remove();
      });
    cleanFallbacks();
    // Helmet opdaterer head efter effekten; observer også de udskudte tags.
    const observer = new MutationObserver(cleanFallbacks);
    observer.observe(document.head, { childList: true, attributes: true });
    return () => observer.disconnect();
  }, [description, url, image, type, title, noindex]);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={url} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="da_DK" />
      {image && <meta property="og:image" content={image} />}
      {image && <meta property="og:image:alt" content={title} />}
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === "article" && articleSection && (
        <meta property="article:section" content={articleSection} />
      )}
      {type === "article" && articleTags?.map((t) => (
        <meta key={t} property="article:tag" content={t} />
      ))}
      <meta name="twitter:card" content={image ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      {image && <meta name="twitter:image" content={image} />}
      {noindex && <meta name="robots" content="noindex,follow" />}
      {blocks.map((b, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(b)}</script>
      ))}
    </Helmet>
  );
}

export const SITE = { url: SITE_URL, name: SITE_NAME };
